/**
 * Typing a figure into the 3B, with its note (C-03, FR-E18, V-d).
 *
 * Four cells are a person's decision and not ProGST's: 4(B)(1) — the reversal under rules
 * 38, 42 and 43 and section 17(5), worked out from the inputs listed beside it — 4(B)(2),
 * and additions to 4(D)(1) and 4(D)(2). A figure without a reason is the one thing V-d's
 * answer forbids, so the note is required here and by the service. The newest entry for a
 * cell is the one the 3B reads; a correction is a new entry, and clearing a figure is an
 * entry of zeros with a note saying why.
 */

import { Alert, Button, Card, Textarea } from '@fiveox/ui'
import { useState } from 'react'

import { ApiError, periods } from '../lib/api'
import { TYPED_CELLS, paiseFromRupees } from '../lib/gstr3bForm'

const HEADS = [
  { key: 'igst', label: 'Integrated tax (₹)' },
  { key: 'cgst', label: 'Central tax (₹)' },
  { key: 'sgst', label: 'State/UT tax (₹)' },
  { key: 'cess', label: 'Cess (₹)' },
] as const

export function Gstr3bTypedFigure({
  periodId,
  onSaved,
}: {
  periodId: string
  onSaved: () => void | Promise<void>
}) {
  const [cell, setCell] = useState<string>(TYPED_CELLS[0]?.id ?? '4B(1)')
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(
    null,
  )

  const heads: Record<string, number> = {}
  let unreadable: string | null = null
  for (const head of HEADS) {
    const paise = paiseFromRupees(amounts[head.key] ?? '')
    if (paise === null) unreadable = head.label
    else heads[head.key] = paise
  }

  const save = async () => {
    setSaving(true)
    try {
      const saved = await periods.typeGstr3bFigure(periodId, { cell, heads, note })
      const label = TYPED_CELLS.find((c) => c.id === cell)?.label ?? cell
      setMessage({
        tone: 'success',
        text:
          `${label}: figure recorded with its note.` +
          (saved.data.demoted_periods.length > 0
            ? ' The GSTR-3B was past preparation and has gone back for preparing again.'
            : ''),
      })
      setAmounts({})
      setNote('')
      await onSaved()
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'The figure could not be recorded.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="mb-5 p-4">
      <p className="gst-panel-h mb-1">Type a figure a person decides</p>
      <p className="gst-meta mb-3">
        ProGST does not compute these. Work the figure out from the inputs listed above, type it
        with a note saying how, and it prints in the form with your note beside it.
      </p>
      {message && (
        <Alert tone={message.tone} className="mb-3">
          {message.text}
        </Alert>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="gst-label">
          Cell
          <select
            className="gst-select"
            value={cell}
            onChange={(e) => setCell(e.target.value)}
            aria-label="The cell being typed"
          >
            {TYPED_CELLS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        {HEADS.map((head) => (
          <label key={head.key} className="gst-label">
            {head.label}
            <input
              className="gst-input w-32"
              inputMode="decimal"
              placeholder="0.00"
              value={amounts[head.key] ?? ''}
              onChange={(e) => setAmounts({ ...amounts, [head.key]: e.target.value })}
            />
          </label>
        ))}
      </div>
      <div className="mt-2">
        <Textarea
          label="Note — how the figure was worked out (required)"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {unreadable && (
        <p className="gst-meta mt-1">
          {unreadable} is not an amount in rupees and paise.
        </p>
      )}
      <Button
        type="button"
        size="sm"
        className="mt-2"
        disabled={saving || !note.trim() || unreadable !== null}
        onClick={() => void save()}
      >
        {saving ? 'Recording…' : 'Record the figure'}
      </Button>
    </Card>
  )
}
